import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, formatApiError } from '@/auth/AuthContext';
import { Seo } from '@/components/site/Seo';

export default function Register() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect away if already signed in
  useEffect(() => {
    if (user) navigate('/account', { replace: true });
  }, [user, navigate]);

  if (user) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await register(name, email, password);
      navigate('/account');
    } catch (err) {
      setError(formatApiError(err, 'Could not create the account. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="min-h-[70vh] flex items-center bg-brand-bone" data-testid="page-register">
      <Seo title="Create an Account | HearthOak Construction" description="Create a HearthOak Construction account to track your estimate requests." path="/register" noindex />
      <div className="container-x max-w-md py-16">
        <div className="eyebrow mb-3">Account</div>
        <h1 className="h2">Create an account</h1>
        <p className="body mt-3">Track your estimate requests and follow-ups in one place.</p>
        <button
          type="button"
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + '/account';
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="btn-secondary w-full mt-8 flex items-center justify-center gap-2"
          data-testid="google-register-button"
        >
          Continue with Google
        </button>
        <div className="flex items-center gap-3 my-6">
          <div className="h-px flex-1 bg-brand-border" />
          <span className="font-sans text-xs uppercase tracking-[0.18em] text-brand-muted">or</span>
          <div className="h-px flex-1 bg-brand-border" />
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="hoc-input" data-testid="register-name-input" autoFocus />
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="hoc-input" data-testid="register-email-input" />
          <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (8+ characters)" className="hoc-input" data-testid="register-password-input" />
          <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm password" className="hoc-input" data-testid="register-confirm-input" />
          {error && <p className="font-sans text-sm text-brand-maroon" data-testid="register-error">{error}</p>}
          <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="register-submit">
            {loading ? 'Creating account…' : 'Create account'}
          </button>
          <p className="text-center font-sans text-sm text-brand-muted">
            Already have an account?{' '}
            <Link to="/login" className="text-brand-maroon hover:underline underline-offset-4" data-testid="register-login-link">Sign in</Link>
          </p>
        </form>
      </div>
    </section>
  );
}
